import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import moment from "moment";
import classes from "./RightPaneContent.scss";

import {
  DropdownMenu,
  Dropdown,
  ButtonDisclosure,
  RadioButton,
  List,
  IconButton,
  Button,
  DSProvider,
} from "@toddle-edu/ds-web";
import {
  AddOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
} from "@toddle-edu/ds-icons";

import FieldInput from "../FieldInput";
import MealMapping from "../MealMapping";
import {
  ADD_NEW_OPTION,
  DELETE_OPTION,
  UPDATE_FIELD_DATASET,
  UPDATE_FIELD_ENTITIES,
  UPDATE_METADATA,
  UPDATE_OPTION_VALUE,
  UPDATE_OPTIONS,
} from "../../modules/RoutineTemplatesModule";
import { FIELD_TYPE_ENUM } from "../CreateRoutineTemplate/CreateRoutineTemplate";
import { getFieldDropdownOptions } from "../../utils";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

const styles = {
  dropdownContainer: {
    borderBottom: "1px solid var(--border-secondary)",
  },
  addButtonStyle: { marginTop: "16px" },
};

export const RightPaneContent = React.memo(
  ({
    selectedFieldId,
    templateFields,
    onChangeField,
    mealMenus,
    updateMealMenus,
    dispatch,
    setIsTemplateModified,
  }) => {
    const { t } = useTranslation(["common", "journal"]);
    const [showMealMapping, setShowMealMapping] = useState(false);
    const [searchText, setSearchText] = useState("");

    const field = useMemo(() => {
      return _.find(templateFields, field => field.id === selectedFieldId);
    }, [selectedFieldId, templateFields]);

    const isSectionHeader =
      _.get(field, "type") === FIELD_TYPE_ENUM.SECTION_HEADER;

    const hasDataSet = _.includes(
      [
        FIELD_TYPE_ENUM.SINGLE_SELECT,
        FIELD_TYPE_ENUM.MULTI_SELECT,
        FIELD_TYPE_ENUM.DROPDOWN,
      ],
      _.get(field, "type")
    );

    const fieldTypeOptions = useMemo(() => {
      return getFieldDropdownOptions({ t });
    }, [t]);

    const [selectedValue, selectedIcon] = useMemo(() => {
      const selectedOption = _.find(
        fieldTypeOptions,
        fieldItem => fieldItem.key === _.get(field, "type")
      );
      return [_.get(selectedOption, "label"), _.get(selectedOption, "icon")];
    }, [field]);

    const onEditOption = option => value => {
      setIsTemplateModified(true);
      dispatch({
        type: UPDATE_OPTION_VALUE,
        payload: {
          fieldId: selectedFieldId,
          displaySequence: _.get(option, "displaySequence"),
          value,
        },
      });
    };

    const updateSearchText = _.debounce(value => setSearchText(value), 500);

    const onDragAndDrop = newOptions => {
      const reorderedOptions = _.map(newOptions, ({ optionValue }, index) => {
        return { value: optionValue, displaySequence: index + 1 };
      });
      setIsTemplateModified(true);
      dispatch({
        type: UPDATE_OPTIONS,
        payload: {
          fieldId: _.get(field, "id"),
          values: reorderedOptions,
        },
      });
    };

    const onDeleteOption = option => e => {
      e?.stopPropagation();
      setIsTemplateModified(true);
      dispatch({
        type: DELETE_OPTION,
        payload: {
          fieldId: _.get(field, "id"),
          displaySequence: _.get(option, "displaySequence"),
        },
      });
    };

    const onAddNewOption = () => {
      setIsTemplateModified(true);
      dispatch({
        type: ADD_NEW_OPTION,
        payload: {
          id: _.get(field, "id"),
        },
      });
    };

    const onClickOutside = value => {
      dispatch({
        type: UPDATE_FIELD_ENTITIES,
        payload: {
          id: _.get(field, "id"),
          label: !_.isEmpty(value) ? value : t("journal:untitled_section"),
        },
      });
    };

    const onEditLabel = value => {
      setIsTemplateModified(true);
      if (hasDataSet) {
        dispatch({
          type: UPDATE_FIELD_DATASET,
          payload: {
            id: _.get(field, "id"),
            title: value,
          },
        });
      } else {
        dispatch({
          type: UPDATE_FIELD_ENTITIES,
          payload: {
            id: _.get(field, "id"),
            label: value,
          },
        });
      }
    };

    const onEditStartTimeLabel = value => {
      setIsTemplateModified(true);
      dispatch({
        type: UPDATE_METADATA,
        payload: {
          id: _.get(field, "id"),
          startTimePlaceholder: value,
        },
      });
    };

    const onEditMinCount = value => {
      setIsTemplateModified(true);
      dispatch({
        type: UPDATE_METADATA,
        payload: {
          id: _.get(field, "id"),
          minCount: value,
        },
      });
    };

    const onEditMaxCount = value => {
      setIsTemplateModified(true);
      dispatch({
        type: UPDATE_METADATA,
        payload: {
          id: _.get(field, "id"),
          maxCount: value,
        },
      });
    };

    const onEditInterval = value => {
      setIsTemplateModified(true);
      dispatch({
        type: UPDATE_METADATA,
        payload: {
          id: _.get(field, "id"),
          stepInterval: value,
        },
      });
    };

    const onEditEndTimeLabel = value => {
      setIsTemplateModified(true);
      dispatch({
        type: UPDATE_METADATA,
        payload: {
          id: _.get(field, "id"),
          endTimePlaceholder: value,
        },
      });
    };

    const onEditPlaceholder = value => {
      setIsTemplateModified(true);
      dispatch({
        type: UPDATE_FIELD_ENTITIES,
        payload: {
          id: _.get(field, "id"),
          placeholder: value,
        },
      });
    };

    const onChangeAutoCompleteTime = value => () => {
      setIsTemplateModified(true);
      dispatch({
        type: UPDATE_METADATA,
        payload: {
          id: _.get(field, "id"),
          autoCalculateTotalDuration: value,
        },
      });
    };

    const onAddMenu = () => {
      setIsTemplateModified(true);
      setShowMealMapping(true);
    };

    const closeMealMapping = () => {
      setShowMealMapping(false);
    };

    const getPopupContainer = el => {
      return el;
    };

    const onRemoveMenu = menuId => () => {
      const updatedMenus = _.filter(mealMenus, menu => {
        const templateId = _.get(menu, "id");
        return templateId !== menuId;
      });
      setIsTemplateModified(true);
      updateMealMenus(updatedMenus, _.get(field, "id"));
    };

    const onChangeSelectedField = e => {
      const fieldType = e.key;
      setIsTemplateModified(true);
      if (fieldType !== _.get(field, "type")) {
        onChangeField({
          fieldId: selectedFieldId,
          previousDisplaySequence: _.get(field, "displaySequence"),
          fieldType,
        });
      }
    };

    const listOptions = useMemo(() => {
      const options = _.get(field, "dataSet.values");
      return _.map(_.sortBy(options, "displaySequence"), (option, index) => {
        return {
          id: `option-${index}`,
          key: `option-${index}`,
          displaySequence: _.get(option, "displaySequence"),
          optionValue: _.get(option, "value"),
          value: (
            <FieldInput
              testIdPrefix={`routine-templates-rightpanecontent-optioninput-${_.get(
                field,
                "id"
              )}`}
              key={`option-input-${_.get(option, "displaySequence")}`}
              initialValue={_.get(option, "value")}
              setOnBlur={true}
              onEditValue={onEditOption(option)}
            />
          ),
          suffix: (
            <IconButton
              testId={`routine-templates-rightpanecontent-optiondelete-${_.get(
                field,
                "id"
              )}`}
              key={`option-delete-${_.get(option, "displaySequence")}`}
              onClick={onDeleteOption(option)}
              icon={<DeleteOutlined />}
              type="plain"
              variant="neutral"
            />
          ),
          style: {
            borderBottom: "0",
          },
        };
      });
    }, [_.get(field, "dataSet.values")]);

    return (
      <>
        <DSProvider dsVersion="2.0">
          <div
            className={classes.fieldWrapper}
            style={styles.dropdownContainer}
            data-test-id="routine-templates-rightpanecontent-fieldtype"
          >
            <label className="text-label">{t("journal:type_of_field")}</label>
            <Dropdown
              getPopupContainer={getPopupContainer}
              overlay={
                <DropdownMenu
                  value={_.get(field, "type")}
                  showSelection={true}
                  selectionType="tick"
                  onClick={onChangeSelectedField}
                  options={fieldTypeOptions}
                  shouldStopPropagation={true}
                  testId="routine-templates-rightpanecontent-fieldtypemenu"
                />
              }
              placement={"bottomRight"}
            >
              <ButtonDisclosure
                icon={selectedIcon}
                variant="neutral"
                type="outlined"
                size="medium"
                isFullWidth={true}
                testId="routine-templates-rightpanecontent-fieldtypebutton"
              >
                {selectedValue}
              </ButtonDisclosure>
            </Dropdown>
          </div>
          <div>
            {!_.includes([FIELD_TYPE_ENUM.MEAL], _.get(field, "type")) ? (
              <div
                className={classes.fieldWrapper}
                data-test-id="routine-templates-rightpanecontent-fieldlabel"
              >
                <FieldInput
                  testIdPrefix="routine-templates-rightpanecontent-field"
                  label={
                    isSectionHeader
                      ? t("journal:header_text")
                      : t("journal:label_of_field")
                  }
                  initialValue={
                    hasDataSet
                      ? _.get(field, "dataSet.title", "")
                      : _.get(field, "label", "")
                  }
                  name={"name"}
                  onEditValue={onEditLabel}
                  isRequired={true}
                  placeholder={t("journal:type_here")}
                  size={"medium"}
                  testId={`routine-templates-rightpanecontent-title`}
                />
              </div>
            ) : null}
            {_.includes(
              [
                FIELD_TYPE_ENUM.SHORT_ANSWER,
                FIELD_TYPE_ENUM.PARAGRAPH,
                FIELD_TYPE_ENUM.TIME,
              ],
              _.get(field, "type")
            ) ? (
              <div className={classes.fieldWrapper}>
                <FieldInput
                  testIdPrefix={`routine-templates-rightpanecontent-placeholder-${_.get(
                    field,
                    "id"
                  )}`}
                  label={t("journal:placeholder_text")}
                  initialValue={_.get(field, "placeholder", "")}
                  placeholder={t("journal:type_here")}
                  onEditValue={onEditPlaceholder}
                  size={"medium"}
                  testId={`routine-templates-rightpanecontent-placeholder`}
                />
              </div>
            ) : null}
            {_.get(field, "type") === FIELD_TYPE_ENUM.TIME_RANGE ? (
              <>
                <div className={classes.fieldWrapper}>
                  <FieldInput
                    testIdPrefix={`routine-templates-rightpanecontent-starttimeplaceholder-${_.get(
                      field,
                      "id"
                    )}`}
                    label={t("journal:placeholder_of_start_time")}
                    initialValue={_.get(field, "metadata.startTimePlaceholder")}
                    placeholder={t("journal:type_here")}
                    onEditValue={onEditStartTimeLabel}
                    size={"medium"}
                    testId={`routine-templates-rightpanecontent-starttimeplaceholder`}
                  />
                </div>
                <div className={classes.fieldWrapper}>
                  <FieldInput
                    testIdPrefix={`routine-templates-rightpanecontent-endtimeplaceholder-${_.get(
                      field,
                      "id"
                    )}`}
                    label={t("journal:placeholder_of_end_time")}
                    initialValue={_.get(field, "metadata.endTimePlaceholder")}
                    onEditValue={onEditEndTimeLabel}
                    placeholder={t("journal:type_here")}
                    size={"medium"}
                    testId={`routine-templates-rightpanecontent-endtimeplaceholder`}
                  />
                </div>
                <div className={classes.fieldWrapper}>
                  <label className="text-label">
                    {t("journal:auto_calc_total_duration")}
                  </label>
                  <div className={classes.radioButtonsWrapper}>
                    <RadioButton
                      testId={`routine-templates-rightpanecontent-autocaluclatetime-yes-${_.get(
                        field,
                        "id"
                      )}`}
                      checked={
                        _.get(field, "metadata.autoCalculateTotalDuration") ===
                        true
                      }
                      onClick={onChangeAutoCompleteTime(true)}
                      size="medium"
                    >
                      {t("common:yes")}
                    </RadioButton>
                    <RadioButton
                      testId={`routine-templates-rightpanecontent-autocaluclatetime-no-${_.get(
                        field,
                        "id"
                      )}`}
                      checked={
                        _.get(field, "metadata.autoCalculateTotalDuration") ===
                        false
                      }
                      onClick={onChangeAutoCompleteTime(false)}
                      size="medium"
                    >
                      {t("common:no")}
                    </RadioButton>
                  </div>
                </div>
              </>
            ) : null}

            {_.get(field, "type") === FIELD_TYPE_ENUM.STEPCOUNT ? (
              <>
                <div className={classes.fieldWrapper}>
                  <FieldInput
                    testIdPrefix={`routine-templates-rightpanecontent-stepcountmin-${_.get(
                      field,
                      "id"
                    )}`}
                    type="number"
                    label={t("journal:minimum_count")}
                    initialValue={_.get(field, "metadata.minCount")}
                    placeholder={t("journal:type_here")}
                    onEditValue={onEditMinCount}
                    size={"medium"}
                  />
                </div>
                <div className={classes.fieldWrapper}>
                  <FieldInput
                    testIdPrefix={`routine-templates-rightpanecontent-stepcountmax-${_.get(
                      field,
                      "id"
                    )}`}
                    type="number"
                    label={t("journal:max_count")}
                    initialValue={_.get(field, "metadata.maxCount")}
                    onEditValue={onEditMaxCount}
                    placeholder={t("journal:type_here")}
                    size={"medium"}
                  />
                </div>
                <div className={classes.fieldWrapper}>
                  <FieldInput
                    testIdPrefix={`routine-templates-rightpanecontent-stepcountinterval-${_.get(
                      field,
                      "id"
                    )}`}
                    type="number"
                    label={t("journal:step_interval")}
                    initialValue={_.get(field, "metadata.stepInterval")}
                    onEditValue={onEditInterval}
                    placeholder={t("journal:type_here")}
                    size={"medium"}
                  />
                </div>
              </>
            ) : null}
            {_.get(field, "type") === FIELD_TYPE_ENUM.MEAL ? (
              <div className={classes.mealOptionsContainer}>
                <label className="text-label">
                  {t("journal:choose_food_menu")}
                </label>
                {!_.isEmpty(mealMenus) ? (
                  <div className={classes.mealOptions}>
                    {_.map(mealMenus, (menu, index) => {
                      return (
                        <div key={`menu-${_.get(menu, "id")}`}>
                          <div className={classes.listOptionContainer}>
                            <div className={classes.listTitle}>
                              <label className="text-label">
                                {`Menu ${index + 1} - ${_.get(menu, "title")}`}
                              </label>
                              <label className="text-body-s">
                                {t("journal:applicable_from_start_to_end", {
                                  startDate: moment(
                                    _.get(menu, "startDate"),
                                    DATE_TIME_CONSTANTS.YYYY_MM_DD
                                  ).format(DATE_TIME_CONSTANTS.DD_MMM),
                                  endDate: moment(
                                    _.get(menu, "endDate"),
                                    DATE_TIME_CONSTANTS.YYYY_MM_DD
                                  ).format(DATE_TIME_CONSTANTS.DD_MMM),
                                })}
                              </label>
                            </div>
                            <div className={classes.iconContainer}>
                              <MinusCircleOutlined
                                testId={`routine-templates-rightpanecontent-removemeals-${_.get(
                                  menu,
                                  "id"
                                )}`}
                                variant="subtle"
                                onClick={onRemoveMenu(_.get(menu, "id"))}
                              />
                            </div>
                          </div>
                          {index + 1 !== _.size(mealMenus) ? (
                            <div className={classes.divider} />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
                <Button
                  testId={`routine-templates-rightpanecontent-addmealmenu`}
                  style={styles.addButtonStyle}
                  onClick={onAddMenu}
                  icon={<AddOutlined />}
                  type="inline"
                  variant="progressive"
                >
                  {t("journal:add_meal_plan")}
                </Button>
              </div>
            ) : null}
            {_.includes(
              [
                FIELD_TYPE_ENUM.SINGLE_SELECT,
                FIELD_TYPE_ENUM.MULTI_SELECT,
                FIELD_TYPE_ENUM.DROPDOWN,
                FIELD_TYPE_ENUM.MEAL,
              ],
              _.get(field, "type")
            ) ? (
              <div className={classes.listOptionsContainer}>
                <div>
                  <label className="text-label">
                    {_.get(field, "type") === FIELD_TYPE_ENUM.MEAL
                      ? t("journal:options_for_observation_and_consumption")
                      : t("journal:options")}
                  </label>
                  <List
                    listItemClassName={classes.listItem}
                    data={listOptions}
                    onChange={onDragAndDrop}
                    isDraggable={true}
                  />
                </div>
                <Button
                  style={styles.addButtonStyle}
                  onClick={onAddNewOption}
                  icon={<AddOutlined />}
                  type="inline"
                  variant="progressive"
                  testId={`routine-templates-rightpanecontent-addnewoption`}
                >
                  {FIELD_TYPE_ENUM.MEAL === _.get(field, "type")
                    ? t("journal:add_observation")
                    : t("journal:add_option")}
                </Button>
              </div>
            ) : null}
          </div>
        </DSProvider>
        {showMealMapping ? (
          <MealMapping
            field={field}
            mealMenusTagged={mealMenus}
            updateMealMenus={updateMealMenus}
            searchText={searchText}
            onSearchMeal={updateSearchText}
            closeMealMapping={closeMealMapping}
          />
        ) : null}
      </>
    );
  }
);

export default RightPaneContent;
RightPaneContent.displayName = "RightPaneContent";
